import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { downloadResponseAsFile } from '@/lib/utils';
import type {
  Invoice,
  InvoiceContents,
  InvoiceContentsInput,
  PaymentTerms,
  RegenerateInvoicePreview,
} from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';
import { invalidateJobBilling } from '@/hooks/jobBillingKeys';

export const invoiceKeys = {
  all: ['invoices'] as const,
  byJob: (jobId: string) => [...invoiceKeys.all, 'job', jobId] as const,
  // Keyed by the intended cash flag too: the preview answers a DIFFERENT
  // question per switch position (which number the invoice would take), so the
  // two answers must not share a cache entry.
  regeneratePreview: (invoiceId: string, isCash?: boolean) =>
    [...invoiceKeys.all, 'regenerate-preview', invoiceId, isCash ?? null] as const,
  regeneratePreviews: (invoiceId: string) =>
    [...invoiceKeys.all, 'regenerate-preview', invoiceId] as const,
  contents: (invoiceId: string) => [...invoiceKeys.all, 'contents', invoiceId] as const,
};

export function useJobInvoices(jobId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.byJob(jobId!),
    queryFn: () => apiClient.get<Invoice[]>(`/jobs/${jobId}/invoices`),
    enabled: !!jobId,
  });
}

export function useCreateInvoice(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      // `paymentTerms` is common to every arm: terms are a property of the
      // invoice, not of how its amount was arrived at.
      data: { paymentTerms?: PaymentTerms } & (
        | { kind: 'final'; amount?: number; description?: string; isCash?: boolean }
        | { kind: 'partial'; source?: 'custom'; amount: number; description?: string; isCash?: boolean }
        | { kind: 'partial'; source: 'lineItems'; lineItemIds: string[]; description?: string; isCash?: boolean }
      ),
    ) => {
      return apiClient.post<Invoice>('/invoices', { ...data, jobId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(jobId) });
      // A new invoice moves the job's rollup counts (not_billed → drafted), and
      // the Billing tab's cards read that rollup, not this list.
      invalidateJobBilling(queryClient, jobId);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// Update payload for an invoice. Status and notes are independent — the route
// only updates the fields the caller actually sends, so passing just { notes }
// won't reset status (and vice versa). Pass `silent: true` to skip the toast
// (useful when the caller is debouncing many small writes, e.g. the notes
// textarea on every keystroke commit).
type UpdateInvoiceVars = {
  id: string;
  status?: 'unpaid' | 'paid' | 'voided';
  notes?: string | null;
  /** `YYYY-MM-DD` to record when the invoice went out, or null to clear it. */
  sentAt?: string | null;
  /**
   * A FREE-FORM partial's customer-facing figure and text. The server refuses
   * these on a final or a line-item partial, whose amounts it derives.
   */
  amount?: number;
  description?: string | null;
  silent?: boolean;
};

export function useUpdateInvoice(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, status, notes, sentAt, amount, description }: UpdateInvoiceVars) => {
      const body: Record<string, unknown> = {};
      if (status !== undefined) body.status = status;
      if (notes !== undefined) body.notes = notes;
      if (sentAt !== undefined) body.sentAt = sentAt;
      if (amount !== undefined) body.amount = amount;
      if (description !== undefined) body.description = description;
      return apiClient.patch<Invoice>(`/invoices/${id}`, body);
    },
    // Optimistically patch the cached row so the change shows immediately,
    // instead of waiting for the PATCH + refetch round-trip (which made notes
    // and status changes feel laggy). Mirrors the server's paidAt handling so
    // the Paid column updates in lockstep with status.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: invoiceKeys.byJob(jobId) });
      const previous = queryClient.getQueryData<Invoice[]>(invoiceKeys.byJob(jobId));
      queryClient.setQueryData<Invoice[]>(invoiceKeys.byJob(jobId), (old) =>
        old?.map((inv) => {
          if (inv.id !== variables.id) return inv;
          const next = { ...inv };
          if (variables.notes !== undefined) next.notes = variables.notes;
          // `amount` is deliberately NOT patched here, for the same reason as
          // `sentAt` below: the server derives `outstanding` and `collected`
          // from it, so writing the amount alone shows a total that does not
          // match the balance beside it. `description` is skipped with it to
          // keep one round-trip rather than two renders of the same row.
          //
          // `sentAt` is deliberately NOT patched here. It decides `lifecycle`,
          // which the server derives, so writing one without the other shows a
          // Draft badge beside a sent date — a state no invoice is ever in.
          // onSettled's invalidation brings both back together.
          if (variables.status !== undefined) {
            next.status = variables.status;
            if (variables.status === 'paid') {
              if (inv.status !== 'paid') next.paidAt = new Date().toISOString();
            } else {
              next.paidAt = null;
            }
          }
          return next;
        }),
      );
      return { previous };
    },
    onError: (error: Error, _variables, context) => {
      // Roll back to the pre-mutation snapshot, then surface the error.
      if (context?.previous) {
        queryClient.setQueryData(invoiceKeys.byJob(jobId), context.previous);
      }
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
    onSuccess: (_data, variables) => {
      if (!variables.silent) {
        toast({ title: 'Invoice updated' });
      }
    },
    // Reconcile with the server (e.g. exact paidAt, cache-busting side effects).
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(jobId) });
      // status, sentAt and a free-form partial's amount are all rollup inputs:
      // voiding drops the invoice out of `invoiced`, and the sent date is what
      // puts it in.
      invalidateJobBilling(queryClient, jobId);
    },
  });
}

export function useDeleteInvoice(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.delete(`/invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(jobId) });
      invalidateJobBilling(queryClient, jobId);
      toast({ title: 'Invoice deleted' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// What regenerating a final invoice would change. Fetched when the confirm
// dialog opens so the user sees the new total before overwriting the record.
// `staleTime: 0` — the breakdown may have changed since the last look, and this
// number is the whole reason the dialog exists.
export function useRegenerateInvoicePreview(
  invoiceId: string | undefined,
  enabled: boolean,
  isCash?: boolean,
) {
  return useQuery({
    queryKey: invoiceKeys.regeneratePreview(invoiceId!, isCash),
    queryFn: () =>
      apiClient.get<RegenerateInvoicePreview>(
        `/invoices/${invoiceId}/regenerate-preview${isCash === undefined ? '' : `?isCash=${isCash}`}`,
      ),
    enabled: !!invoiceId && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

// Rebuild an unpaid final invoice in place from the job's current breakdown —
// the one-click replacement for void → delete → recreate. The row keeps its id,
// status and notes; the amount, source doc and date move.
//
// `isCash` is the one exception to "the number never changes": changing the
// cash flag recodes the number into the matching sequence, because that C is
// how a cash invoice is recognised. Omit it to leave both alone.
//
// `keepVoidedCopy` leaves the pre-rebuild invoice behind as a voided row under
// the same number, so the job's list keeps a record of the amount that was
// replaced rather than it being overwritten.
export function useRegenerateInvoice(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      invoiceId,
      isCash,
      keepVoidedCopy,
    }: {
      invoiceId: string;
      isCash?: boolean;
      keepVoidedCopy?: boolean;
    }) => {
      return apiClient.post<Invoice>(`/invoices/${invoiceId}/regenerate`, {
        ...(isCash === undefined ? {} : { isCash }),
        ...(keepVoidedCopy ? { keepVoidedCopy: true } : {}),
      });
    },
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(jobId) });
      // Every cached preview for this invoice, not just the one that was
      // confirmed — the other switch position's answer is stale too.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.regeneratePreviews(invoice.id) });
      // The whole point of a regenerate is that the amount moved, so `invoiced`
      // and `outstanding` moved with it (NEW-242).
      invalidateJobBilling(queryClient, jobId);
      toast({ title: `${invoice.invoiceNumber} regenerated` });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * What the Edit dialog opens with.
 *
 * `staleTime: 0`, for the same reason the regenerate preview uses it: the app
 * sets a 5-minute global staleTime (App.tsx), and which of this invoice's lines
 * still exist in the breakdown is exactly what the dialog cannot afford to be
 * stale about.
 */
export function useInvoiceContents(invoiceId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.contents(invoiceId!),
    queryFn: () => apiClient.get<InvoiceContents>(`/invoices/${invoiceId}/contents`),
    enabled: !!invoiceId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Replace what a partial invoice says, keeping its id and its number.
 *
 * Invalidates the job's invoice LIST rather than patching the row: editing a
 * SENT invoice can mint a second row — the superseding voided copy the backend
 * writes when the amount moves — which no optimistic patch could predict.
 */
export function useUpdateInvoiceContents(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ invoiceId, ...contents }: { invoiceId: string } & InvoiceContentsInput) =>
      apiClient.put<Invoice>(`/invoices/${invoiceId}/contents`, contents),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(jobId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.contents(invoice.id) });
      // The amount can have moved, so `invoiced` and `outstanding` moved with
      // it — NEW-242 is what happens without this.
      invalidateJobBilling(queryClient, jobId);
      toast({ title: `${invoice.invoiceNumber} updated` });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export async function downloadInvoicePdf(invoiceId: string, invoiceNumber: string) {
  const response = await apiClient.get<Response>(`/invoices/${invoiceId}/pdf`);
  await downloadResponseAsFile(response, `${invoiceNumber}.pdf`);
}

/**
 * FR-4.7's manual fallback: an invoice is born a draft and is marked sent
 * explicitly. Idempotent — re-marking keeps the original send date.
 */
export function useMarkInvoiceSent(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => apiClient.post<Invoice>(`/invoices/${id}/mark-sent`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(jobId) });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast({ title: 'Marked as sent' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}
